import React, { useCallback, useMemo } from 'react';
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  OnChangeFn,
  RowSelectionState,
  useReactTable,
} from '@tanstack/react-table';
import {
  FavoriteEntityType,
  IDictionaryNamedCohortFieldsFragment,
  PermissionKey,
  IScheduleFieldsFragment,
} from '../../../utils/apollo/graphqlSchema';
import { EntityCategories } from '../../../components/Categories';
import EditableTextArea from '../../../components/EditableTextArea';
import {
  entityDescriptionSchema,
  entityNameSchema,
} from '../../../components/EditableTextArea/validation';
import { assertNotNil, assertCondition } from '../../../utils/types';
import { FavoriteVersionEntity } from '../../../components/Favorite';
import FormCreator from '../../pageForm/FormCreator';
import FormEditor from '../../pageForm/FormEditor';
import { useAppSelector } from '../../../redux/hooks';
import BuilderInfo from '../../../components/BuilderInfo';
import usePermission from '../../../utils/hooks/permissionHooks/usePermission';
import Table from '../../../components/Table';
import { cohortTableColumnsConfig } from './tableStylesConfig';
import { globalFormat } from '../../../utils/formatting';
import useSyndicateTimezoneLabel from '../../../utils/hooks/useSyndicateTimezoneLabel';
import EntityInfoPopover from '../../../components/EntityInfoPopover';
import {
  HeaderCheckbox,
  RowCheckbox,
} from '../../../components/Table/TableCheckbox';
import CohortDictionaryActionsMenu from './CohortDictionaryActionsMenu';
import ExecutionHistoryPopover from '../../../components/ExecutionHistoryPopover';
import ScheduleCohortImportedHistory from './ScheduleCohortImportedHistory';
import TimezonePopup from '../../../components/TimezonePopup';
import * as FormUI from '../../../pages/pageForm/FormUI';
import { ScheduleRecurrence } from '../../Schedule/components';
import CohortSchedulerContainer from '../../Dictionary/utils/CohortSchedulerContainer';
import { useShowBraze } from '../../../utils/hooks';

interface IProps {
  namedCohorts: IDictionaryNamedCohortFieldsFragment[];
  schemaId: string;
  onApplyTags: (args: {
    entities: IDictionaryNamedCohortFieldsFragment[];
    newTags: string[];
    isManaged: boolean;
    schemaId: string;
  }) => Promise<void>;
  onChangeName: (
    cohort: IDictionaryNamedCohortFieldsFragment,
    newName: string,
  ) => void;
  onChangeDescription: (
    cohort: IDictionaryNamedCohortFieldsFragment,
    newDescription: string,
  ) => void;
  onSetFavorite: () => void;
  rowSelection: RowSelectionState;
  setRowSelection: OnChangeFn<RowSelectionState>;
  isPermittedToAssignCategory: boolean;
}

const initialState = {
  sorting: [
    {
      id: 'name',
      desc: true, // sort by name in descending order by default
    },
  ],
};

const columnHelper = createColumnHelper<IDictionaryNamedCohortFieldsFragment>();

const CohortDictionaryTable = ({
  namedCohorts,
  schemaId,
  rowSelection,
  setRowSelection,
  onApplyTags,
  onChangeName,
  onChangeDescription,
  onSetFavorite,
  isPermittedToAssignCategory,
}: IProps) => {
  const { dateFormat, timeFormat } = globalFormat.getGlobalFormat();
  const shortTimeZone = useSyndicateTimezoneLabel();
  const isBrazeConnect = useShowBraze();

  const { checkIsPermitted } = usePermission();
  const isPermittedToEditCohort = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.CohortEdit],
    schemaId,
  });

  const schedules: IScheduleFieldsFragment[] = useAppSelector(
    (state) => state.dictionary.scheduleCohort,
  );

  const findScheduleCohort = useCallback(
    (cohortId: string) => {
      return schedules.find((schedule) => {
        assertCondition(schedule.entity.__typename === 'CohortScheduleEntity');
        return schedule.entity.item.id === cohortId;
      });
    },
    [schedules],
  );

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'checkbox',
        header: ({ table }) => <HeaderCheckbox table={table} />,
        cell: (info) => <RowCheckbox row={info.row} />,
        meta: cohortTableColumnsConfig.checkbox,
      }),
      columnHelper.accessor('version', {
        id: 'name',
        header: 'Name',
        cell: (info) => {
          const version = info.getValue();
          assertNotNil(version);

          return (
            <EditableTextArea
              isBold
              disabled={version.isDisabled}
              placeholder="Add Name..."
              value={version.name}
              onChange={(newValue) => onChangeName(info.row.original, newValue)}
              defaultRows={2}
              validation={entityNameSchema}
              isReadOnly={!isPermittedToEditCohort}
            />
          );
        },
        meta: cohortTableColumnsConfig.name,
        enableSorting: true,
      }),
      columnHelper.accessor('version', {
        id: 'description',
        header: 'Description',
        cell: (info) => {
          const version = info.getValue();
          assertNotNil(version);

          return (
            <EditableTextArea
              disabled={version.isDisabled}
              placeholder={'Add Description...'}
              value={version.description}
              onChange={(newValue) =>
                onChangeDescription(info.row.original, newValue)
              }
              validation={entityDescriptionSchema}
              isReadOnly={!isPermittedToEditCohort}
            />
          );
        },
        meta: cohortTableColumnsConfig.description,
        enableSorting: true,
      }),
      columnHelper.accessor('categories', {
        id: 'tags',
        header: 'Tags',
        cell: (info) => {
          const categories = info.getValue();

          return (
            <EntityCategories
              schemaId={schemaId}
              isDisabled={!isPermittedToAssignCategory}
              entityCategories={categories}
              onApply={(newTags, isManaged) =>
                onApplyTags({
                  entities: [info.row.original],
                  newTags,
                  isManaged,
                  schemaId,
                })
              }
              isManaged={info.row.original.isManaged}
            />
          );
        },
        meta: cohortTableColumnsConfig.tags,
        enableSorting: true,
      }),
      columnHelper.accessor('isFavorite', {
        id: 'favorite',
        header: 'Favorite',
        cell: (info) => (
          <FavoriteVersionEntity
            entityType={FavoriteEntityType.Cohort}
            entity={info.row.original}
            onRefresh={onSetFavorite}
          />
        ),
        meta: cohortTableColumnsConfig.favorite,
        enableSorting: true,
      }),
      columnHelper.accessor('version.creator', {
        id: 'creator',
        header: 'Creator',
        cell: (info) => {
          const creator = info.getValue();

          return (
            <FormCreator
              label={creator.name}
              picture={creator.picture}
              createdAt={info.row.original.version?.createdAt}
            />
          );
        },
        meta: cohortTableColumnsConfig.creator,
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          assertNotNil(rowA.original.version);
          assertNotNil(rowB.original.version);
          const creatorNameA = rowA.original.version.creator.name;
          const creatorNameB = rowB.original.version.creator.name;

          return creatorNameA > creatorNameB
            ? 1
            : creatorNameA < creatorNameB
              ? -1
              : 0;
        },
      }),
      columnHelper.accessor('version.editor', {
        id: 'editor',
        header: 'Last Edited',
        cell: (info) => {
          const editor = info.getValue();

          return (
            <FormEditor
              label={editor.name}
              picture={editor.picture}
              lastEdited={info.row.original.version?.editedAt}
            />
          );
        },
        meta: cohortTableColumnsConfig.editor,
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          assertNotNil(rowA.original.version);
          assertNotNil(rowB.original.version);
          const editorNameA = rowA.original.version.editor.name;
          const editorNameB = rowB.original.version.editor.name;

          return editorNameA > editorNameB
            ? 1
            : editorNameA < editorNameB
              ? -1
              : 0;
        },
      }),
      columnHelper.accessor('version.editedAt', {
        id: 'edited-time',
        header: `Last Edit Time (${shortTimeZone})`,
        cell: (info) => {
          const editedAt = info.getValue();

          return (
            <FormUI.DataLabel>
              <TimezonePopup
                timestamp={editedAt as string}
                dateFormat={dateFormat}
                timeFormat={timeFormat}
                placement="bottom-end"
                showTimezone
              />
            </FormUI.DataLabel>
          );
        },
        meta: cohortTableColumnsConfig.creationTime,
        enableSorting: true,
      }),
      ...(isBrazeConnect
        ? [
            columnHelper.display({
              id: 'schedule-recurrence',
              header: 'Recurrence',
              cell: (info) => {
                const cohort = info.row.original;
                const scheduleCohort = findScheduleCohort(cohort.id);

                return scheduleCohort ? (
                  <FormUI.DataLabel $isRightAligned={false}>
                    <ScheduleRecurrence
                      cron={scheduleCohort.cron}
                      timezone={scheduleCohort.timezone}
                    />
                  </FormUI.DataLabel>
                ) : null;
              },
              meta: cohortTableColumnsConfig.scheduleRecurrence,
            }),
            columnHelper.display({
              id: 'last-run',
              header: 'Last Execution',
              cell: (info) => {
                const cohort = info.row.original;
                const scheduleCohort = findScheduleCohort(cohort.id);

                return scheduleCohort?.lastRunAt ? (
                  <FormUI.DataLabel $isRightAligned={false}>
                    <TimezonePopup
                      timestamp={scheduleCohort.lastRunAt}
                      placement="bottom-end"
                      dateFormat={dateFormat}
                      timeFormat={timeFormat}
                      showTimezone
                    />
                  </FormUI.DataLabel>
                ) : null;
              },
              meta: cohortTableColumnsConfig.lastRun,
            }),
          ]
        : []),
      columnHelper.display({
        id: 'actions',
        header: '',
        cell: (info) => {
          const cohort = info.row.original;
          const scheduleCohort = findScheduleCohort(cohort.id);

          return (
            <FormUI.ActionsContainer>
              {scheduleCohort && (
                <ExecutionHistoryPopover>
                  <ScheduleCohortImportedHistory
                    scheduleId={scheduleCohort.id}
                  />
                </ExecutionHistoryPopover>
              )}
              <EntityInfoPopover
                placement="bottom-end"
                closeOnAncestorScroll={true}
              >
                <BuilderInfo
                  kind="namedCohort"
                  fetched={false}
                  cohortId={cohort.id}
                />
              </EntityInfoPopover>
              <CohortDictionaryActionsMenu
                schemaId={schemaId}
                cohort={cohort}
                scheduleCohort={scheduleCohort}
              />
              <CohortSchedulerContainer
                cohortId={cohort.id}
                cohortVersion={cohort.version}
              />
            </FormUI.ActionsContainer>
          );
        },
        meta: cohortTableColumnsConfig.actions,
      }),
    ],
    [
      schemaId,
      isPermittedToAssignCategory,
      isPermittedToEditCohort,
      onApplyTags,
      onChangeName,
      onChangeDescription,
      onSetFavorite,
      isBrazeConnect,
      dateFormat,
      timeFormat,
      shortTimeZone,
      findScheduleCohort,
    ],
  );

  const table = useReactTable({
    data: namedCohorts,
    columns,
    state: {
      rowSelection,
    },
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState,
    getRowId: (row) => row.id,
  });

  return (
    <Table
      headerGroups={table.getHeaderGroups()}
      rows={table.getRowModel().rows}
    />
  );
};

export default CohortDictionaryTable;
